<template>
  <span>
    <template v-for="(token, i) in renderTokens" :key="i">
      <button
        v-if="token.word"
        type="button"
        class="rounded px-0.5 -mx-0.5 border-b border-dashed border-emerald-300 hover:bg-emerald-50 hover:border-emerald-500 transition-colors dark:border-emerald-700 dark:hover:bg-emerald-950 dark:hover:border-emerald-500"
        :class="isOpen(token) ? 'bg-emerald-100 border-emerald-500 dark:bg-emerald-900' : ''"
        :aria-label="`${token.word.vietnamese} — ${token.word.english}`"
        @click.stop="onWordClick(token.word, $event)"
      >{{ token.text }}</button>
      <span v-else>{{ token.text }}</span>
    </template>

    <Teleport to="body">
      <div
        v-if="mine"
        ref="popover"
        class="fixed z-50 w-64 rounded-xl border border-gray-200 bg-white shadow-lg p-4 dark:border-gray-700 dark:bg-gray-900"
        :style="popoverStyle"
        @click.stop
      >
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <p class="text-lg font-bold text-gray-900 leading-tight dark:text-gray-50">{{ mine.word.vietnamese }}</p>
            <p v-if="mine.word.phonetic" class="text-sm text-emerald-700 dark:text-emerald-400">{{ mine.word.phonetic }}</p>
          </div>
          <AudioButton :src="mine.word.audio_url" :text="mine.word.vietnamese" :show-slow="true" class="shrink-0" />
        </div>

        <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">{{ mine.word.english }}</p>
        <p v-if="mine.word.part_of_speech" class="mt-1 text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
          {{ mine.word.part_of_speech }}
        </p>

        <div class="mt-3 flex items-center gap-2">
          <button
            type="button"
            class="flex-1 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors"
            :class="saved
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800'
              : 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'"
            @click="onToggleSaved"
          >
            {{ saved ? '&#10003; In your deck' : '+ Add to deck' }}
          </button>
          <!-- In-app word page, NOT /vietnamese/:slug. That one is the public
               phrasebook: anonymous, SEO-oriented, and it pitches signup — which
               reads as though the app forgot a signed-in learner. This page shows
               their own tone curve, SRS strength and review schedule. -->
          <a
            :href="`/words/${mine.word.slug || mine.word.id}`"
            class="text-xs text-gray-400 hover:text-emerald-700 hover:underline shrink-0 dark:text-gray-500 dark:hover:text-emerald-400"
          >Details</a>
        </div>
      </div>
    </Teleport>
  </span>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import AudioButton from './AudioButton.vue'
import {
  annotateTexts,
  activeWord,
  openWord,
  closeWord,
  isSaved,
  toggleSaved,
  nextOwnerId,
} from '../../utils/lookup'

const props = defineProps({
  // Raw text to make tappable. Ignored when `tokens` is supplied.
  text: { type: String, default: '' },
  // Pre-annotated tokens. Parents holding several lines should batch one
  // annotateTexts call and pass the results down rather than have each line
  // fetch its own.
  tokens: { type: Array, default: null },
  // Tags saved words with their origin, e.g. { kind: 'story', id: 12 }.
  source: { type: Object, default: () => ({}) },
})

const POPOVER_WIDTH = 256
const POPOVER_MAX_HEIGHT = 220
const EDGE_GAP = 8

const ownerId = nextOwnerId()
const fetched = ref(null)
const popover = ref(null)

const renderTokens = computed(() => props.tokens ?? fetched.value ?? [{ text: props.text }])

// Only the instance that opened the popover renders it, which keeps "one open
// at a time" true without a page-level host component.
const mine = computed(() => (activeWord.value?.ownerId === ownerId ? activeWord.value : null))
const saved = computed(() => (mine.value ? isSaved(mine.value.word.id) : false))

function isOpen(token) {
  return mine.value?.word.id === token.word?.id
}

function onWordClick(word, event) {
  if (mine.value?.word.id === word.id) {
    closeWord()
    return
  }
  openWord(ownerId, word, event.currentTarget.getBoundingClientRect())
}

async function onToggleSaved() {
  if (mine.value) await toggleSaved(mine.value.word, props.source)
}

const popoverStyle = computed(() => {
  const rect = mine.value?.rect
  if (!rect) return {}

  const left = Math.min(
    Math.max(EDGE_GAP, rect.left),
    Math.max(EDGE_GAP, window.innerWidth - POPOVER_WIDTH - EDGE_GAP)
  )

  // Flip above the word when there isn't room below it.
  const fitsBelow = rect.bottom + POPOVER_MAX_HEIGHT + EDGE_GAP < window.innerHeight
  return fitsBelow
    ? { left: `${left}px`, top: `${rect.bottom + EDGE_GAP}px` }
    : { left: `${left}px`, bottom: `${window.innerHeight - rect.top + EDGE_GAP}px` }
})

// Self-fetch only when no tokens were handed down.
watch(
  () => props.text,
  async (text) => {
    if (props.tokens || !text) return
    const [tokens] = await annotateTexts([text])
    fetched.value = tokens
  },
  { immediate: true }
)

function onDocumentClick() {
  if (mine.value) closeWord()
}

function onKeydown(event) {
  if (event.key === 'Escape' && mine.value) closeWord()
}

// The popover is fixed-positioned against a rect captured at open time, so it
// would drift away from its word on scroll. Closing is less work than tracking.
function onScroll() {
  if (mine.value) closeWord()
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKeydown)
  window.addEventListener('scroll', onScroll, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKeydown)
  window.removeEventListener('scroll', onScroll, true)
  if (mine.value) closeWord()
})
</script>
